import {
  createStyles,
  TableCell,
  type WithStyles,
  withStyles,
} from "@material-ui/core";
import clsx from "clsx";
import React, { useMemo } from "react";
import type { CustomTableColumn } from "./customTable.types";

type OwnProps<
  TRow extends Record<string, unknown>,
  TColumn extends CustomTableColumn<TRow, TCols>,
  TCols extends Record<string, string>
> = {
  align?: "left" | "right" | "inherit" | "center" | "justify";
  className?: string;
  column: TColumn;
  row: TRow;
};

type CustomTableCellImplProps<
  TRow extends Record<string, unknown>,
  TColumn extends CustomTableColumn<TRow, TCols>,
  TCols extends Record<string, string>
> = OwnProps<TRow, TColumn, TCols> & WithStyles<typeof styles>;

function CustomTableCellImpl<
  TRow extends Record<string, unknown>,
  TColumn extends CustomTableColumn<TRow, TCols>,
  TCols extends Record<string, string>
>({
  align,
  classes,
  className,
  column,
  row,
  ...rest
}: CustomTableCellImplProps<TRow, TColumn, TCols>) {
  const cellValue = useMemo(() => {
    if (column.type === "custom") {
      return column.renderCell(row);
    }

    const value = column.getRowValue(row);
    return column.renderCell?.(value as never, row) ?? value;
  }, [column, row]);

  const cellClass = clsx(classes.cell, className);

  return (
    <TableCell align={align} className={cellClass} component="td" {...rest}>
      {cellValue}
    </TableCell>
  );
}

const styles = () =>
  createStyles({
    cell: {
      color: "#757575",
      fontSize: "14px",
      fontWeight: 400,
    },
  });

const CustomTableCell = withStyles(styles)(CustomTableCellImpl) as <
  TRow extends Record<string, unknown>,
  TColumn extends CustomTableColumn<TRow, TCols>,
  TCols extends Record<string, string>
>(
  props: OwnProps<TRow, TColumn, TCols>
) => JSX.Element;

export default CustomTableCell;
